import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, Calendar as Cal, MapPin, Globe, Lock, Tag, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LangContext";
import AddToCalendar from "@/components/AddToCalendar";

const LANG_FLAGS = { fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", it: "🇮🇹", pt: "🇵🇹" };

export default function FormationDetail() {
  const { t } = useLang();
  const { slug } = useParams();
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [pickedTicket, setPickedTicket] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [guest, setGuest] = useState({ first_name: "", last_name: "", email: "", company: "" });

  useEffect(() => { api.get(`/formations/${slug}`).then((r) => setF(r.data)).catch(() => {}); }, [slug]);

  const register = async () => {
    if (!user && (!guest.email || !guest.first_name)) { toast.error("Veuillez vous identifier"); return; }
    setRegistering(true);
    try {
      const r = await api.post(`/formations/${f.id}/register`, {
        ticket_id: pickedTicket?.id || (f.tickets?.[0]?.id ?? null),
        ...(!user ? guest : {}),
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else toast.success("Inscription confirmée");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setRegistering(false); }
  };

  if (!f) return <PublicLayout><div className="mx-auto max-w-3xl py-20 text-center text-neutral-500">{t("comp_formationdetail_90")}</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-wine-50 px-2.5 py-0.5 font-bold text-wine-700"><Globe className="h-3 w-3" />{LANG_FLAGS[f.language]} {(f.language || "fr").toUpperCase()}</span>
          {f.audience === "members_only" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-0.5 font-bold text-white"><Lock className="h-3 w-3" />Membres</span>}
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 font-bold text-emerald-700"><Tag className="h-3 w-3" />{(f.tickets || []).some((t) => (t.price || 0) > 0) ? "Payant" : "Gratuit"}</span>
        </div>
        <h1 className="mt-4 text-4xl font-bold text-navy md:text-5xl"><GraduationCap className="mr-2 inline h-9 w-9 text-wine-600" />{f.title}</h1>
        <p className="mt-3 text-lg text-neutral-700">{f.description}</p>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-neutral-600">
          {f.date && <span className="inline-flex items-center gap-1.5"><Cal className="h-4 w-4 text-wine-600" />{new Date(f.date).toLocaleString("fr", { dateStyle: "full", timeStyle: "short" })}</span>}
          {f.format === "digital" ? <span className="inline-flex items-center gap-1.5">💻 Digital</span> : <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-wine-600" />{f.location_name || ""} {f.city ? `· ${f.city}` : ""}</span>}
          {f.date && (
            <AddToCalendar
              title={f.title}
              description={f.description || f.long_description || ""}
              location={[f.location_name, f.address, f.city, f.country].filter(Boolean).join(", ")}
              startDate={f.date}
              endDate={f.end_date}
              virtualLink={f.virtual_link}
              virtualPassword={f.virtual_password}
              testId="formation-add-to-calendar"
            />
          )}
        </div>

        {f.long_description && <div className="mt-8 whitespace-pre-wrap rounded-2xl border border-neutral-100 bg-white p-6 text-sm leading-relaxed text-neutral-800">{f.long_description}</div>}

        {f.virtual_link && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="formation-link">
            <div className="text-xs font-bold uppercase text-emerald-700">{t("comp_formationdetail_91")}</div>
            <a href={f.virtual_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 hover:underline">{f.virtual_link} <ExternalLink className="h-3 w-3" /></a>
            {f.virtual_password && <div className="mt-1 text-xs text-emerald-700">{t("comp_formationdetail_92")} <code className="rounded bg-white px-1.5 py-0.5">{f.virtual_password}</code></div>}
          </div>
        )}

        <div className="mt-8 rounded-3xl border border-wine-100 bg-wine-50/40 p-6" data-testid="formation-register">
          <h3 className="text-lg font-bold text-navy">S&apos;inscrire</h3>
          {(f.tickets || []).length > 0 && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {f.tickets.map((t) => (
                <button key={t.id} onClick={() => setPickedTicket(t)} className={`rounded-xl border-2 p-3 text-left transition-all ${pickedTicket?.id === t.id ? "border-wine-500 bg-white" : "border-neutral-200 bg-white hover:border-wine-300"}`} data-testid={`ticket-${t.id}`}>
                  <div className="font-bold text-neutral-900">{t.name}</div>
                  <div className="text-xs text-neutral-500">{t.price} {(t.currency || "EUR").toUpperCase()}</div>
                </button>
              ))}
            </div>
          )}
          {!user && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <input value={guest.first_name} onChange={(e) => setGuest({ ...guest, first_name: e.target.value })} placeholder={t("comp_formationdetail_93")} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={guest.last_name} onChange={(e) => setGuest({ ...guest, last_name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} type="email" placeholder="Email *" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={guest.company} onChange={(e) => setGuest({ ...guest, company: e.target.value })} placeholder="Entreprise" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            </div>
          )}
          <button onClick={register} disabled={registering} className="mt-4 w-full rounded-full bg-wine-600 px-6 py-3 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="formation-register-btn">
            {registering ? "Inscription…" : (f.tickets || []).some((t) => (t.price || 0) > 0) ? "Réserver et payer →" : "Je m'inscris"}
          </button>
        </div>
      </section>
    </PublicLayout>
  );
}
